import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Lock, Check, Fingerprint, Shield } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function VaultedSessionsFeature() {
  useSEO({
    title: "Vaulted Sessions",
    description: "JWT-signed sessions, secure cookies, automatic expiry. Your authentication lives behind iron.",
    path: "/patrons/vaulted-sessions",
  });
  return (
    <main data-testid="patrons-vaulted" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="patrons-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to the Landing
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 03 / For Patrons · Vaulted Sessions</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              Your session,<br/><span className="italic gold-text">behind iron.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Every y ℶα session is signed with a rotating key, transmitted over TLS 1.3, and stored in a hardened HttpOnly cookie. Sessions auto-expire. Stolen credentials never linger. You sign in once; we keep the door watched.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" className="btn-gold inline-flex items-center gap-2">Open an account <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Session protections</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3"><Lock size={16} className="text-gold"/> HttpOnly · Secure · SameSite cookies</li>
                <li className="flex items-center gap-3"><Fingerprint size={16} className="text-gold"/> JWT bearer signed with HS256</li>
                <li className="flex items-center gap-3"><Shield size={16} className="text-gold"/> Short TTL, server-side revocation</li>
                <li className="flex items-center gap-3"><Shield size={16} className="text-gold"/> TLS 1.3 only, HSTS preloaded</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["JWT-signed bearer", "Tokens cryptographically signed — tampering breaks the seal instantly."],
            ["HttpOnly cookies", "Cookies invisible to JavaScript. XSS cannot exfiltrate your session."],
            ["Automatic expiry", "Sessions die on a timer. Stolen cookies become worthless quickly."],
            ["TLS 1.3 + HSTS", "Every byte encrypted. Browser refuses downgrades."],
            ["Server-side revocation", "Sign out anywhere; the server invalidates immediately."],
            ["Google SSO", "Sign in with Google — inherit passkeys, 2FA, and account recovery."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Quiet. <span className="italic gold-text">Vaulted. Yours.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Enter the vault <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
